import React, { useState, useEffect } from 'react';
import { useStore } from '../states/store';
import { Box, Modal, Fade } from '@mui/material';
import HLBackendV1Api from '../utils/http/api';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import Divider from '@mui/material/Divider';
import { motion } from 'framer-motion';

import 'highcharts/modules/heatmap';
import 'highcharts/modules/bullet';

// Purple theme styles
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '95%',
  maxWidth: '900px',
  background: 'linear-gradient(135deg, #0a0a1a 0%, #0f0f1f 50%, #12122a 100%)',
  borderRadius: '24px',
  boxShadow: '0 25px 50px -12px rgba(139, 92, 246, 0.25)',
  p: { xs: 1.5, md: 2 },
  outline: 'none',
  overflowY: 'auto',
  maxHeight: '85vh',
  border: '1px solid rgba(139, 92, 246, 0.2)',
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#1a1535',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#8b5cf6',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a78bfa',
  },
};

// Inject global purple theme styles for charts
const injectChartStyles = () => {
  const styleId = 'purple-chart-styles';
  if (!document.getElementById(styleId)) {
    const styleEl = document.createElement('style');
    styleEl.id = styleId;
    styleEl.textContent = `
      .highcharts-root {
        font-family: 'Inter', sans-serif;
      }
      .highcharts-title {
        fill: #c4b5fd !important;
        font-weight: 600 !important;
      }
      .highcharts-subtitle {
        fill: #6b6b8d !important;
      }
      .highcharts-legend-item text {
        fill: #c4b5fd !important;
      }
      .highcharts-axis-labels text {
        fill: #94a3b8 !important;
      }
      .highcharts-axis-line, .highcharts-tick {
        stroke: rgba(139, 92, 246, 0.3) !important;
      }
      .highcharts-grid-line {
        stroke: rgba(139, 92, 246, 0.1) !important;
      }
      .highcharts-tooltip-box {
        fill: #1a1535 !important;
        stroke: #8b5cf6 !important;
        stroke-width: 1px !important;
      }
      .highcharts-tooltip text {
        fill: #f3f0ff !important;
      }
    `;
    document.head.appendChild(styleEl);
  }
};

export default function DailyDrawHistory() {
  const isDrawHistoryOpen = useStore((state) => state.isDrawHistoryOpen);
  const closeDrawHistory = useStore((state) => state.closeDrawHistory);
  
  // Chart states with purple theme
  const [pieChartOptions, setPieChartOptions] = useState({
    chart: { backgroundColor: 'transparent' },
    title: { text: '' },
    series: []
  });
  
  const [columnChartOptions, setColumnChartOptions] = useState({
    chart: { backgroundColor: 'transparent' },
    title: { text: '' },
    series: []
  });

  const [heatmapChartOptions, setHeatmapChartOptions] = useState({
    chart: { backgroundColor: 'transparent' },
    title: { text: '' },
    series: []
  });

  const [bulletChartOptions, setBulletChartOptions] = useState({
    chart: { backgroundColor: 'transparent' },
    title: { text: '' },
    series: []
  });

  useEffect(() => {
    injectChartStyles();
  }, []);

  const getDailyDrawHistory = async () => {
    try {
      const response = await HLBackendV1Api.get(`${process.env.BACKEND_API_URL}/hl-game-management/daily-draw-data`);
      
      const { totals, hourlyData } = response.data.results;

      // ---------- PIE CHART OPTIONS (Purple Theme) ----------
      setPieChartOptions({
        chart: {
          type: 'pie',
          backgroundColor: 'transparent',
          height: 300,
          spacingTop: 20,
          style: { fontFamily: 'Inter, sans-serif' }
        },
        title: {
          text: '24-Hour Distribution',
          align: 'center',
          style: { color: '#c4b5fd', fontSize: '16px', fontWeight: '600', letterSpacing: '0.05em' }
        },
        subtitle: {
          text: `Total: ${totals.total.toLocaleString()} | Up: ${totals.totalUpPercentage}% | Down: ${totals.totalDownPercentage}% | Draw: ${totals.totalDrawPercentage}%`,
          style: { color: '#6b6b8d', fontSize: '11px' }
        },
        tooltip: {
          pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b> ({point.y})',
          backgroundColor: '#1a1535',
          borderColor: '#8b5cf6',
          borderRadius: 8,
          style: { color: '#f3f0ff', fontFamily: 'Inter, sans-serif' }
        },
        plotOptions: {
          pie: {
            borderWidth: 3,
            borderColor: '#0f0f1f',
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              format: '{point.name}: {point.y}',
              distance: 20,
              style: { color: '#c4b5fd', textOutline: 'none', fontSize: '12px', fontWeight: '500' }
            },
            showInLegend: true,
            animation: {
              duration: 1000,
              easing: 'easeOutQuad'
            }
          }
        },
        legend: {
          enabled: true,
          align: 'center',
          verticalAlign: 'top',
          itemStyle: { color: '#c4b5fd', fontSize: '12px', fontFamily: 'Inter, sans-serif' },
          symbolRadius: 8,
          symbolHeight: 12,
          symbolWidth: 12
        },
        series: [{
          name: 'Total',
          data: [
            { name: 'Up', y: totals.totalUp, color: '#10b981' },
            { name: 'Down', y: totals.totalDown, color: '#ec4899' },
            { name: 'Draw', y: totals.totalDraw, color: '#f59e0b' }
          ]
        }],
        credits: { enabled: false }
      });

      // ---------- BULLET CHART OPTIONS (Purple Theme) ----------
      setBulletChartOptions({
        chart: {
          type: 'bullet',
          backgroundColor: 'transparent',
          height: 220,
          inverted: true,
          marginLeft: 80,
          style: { fontFamily: 'Inter, sans-serif' }
        },
        title: {
          text: 'Performance Targets',
          align: 'center',
          style: { color: '#c4b5fd', fontSize: '16px', fontWeight: '600', letterSpacing: '0.05em' }
        },
        subtitle: {
          text: 'Current vs Target Percentage',
          style: { color: '#6b6b8d', fontSize: '11px' }
        },
        legend: { enabled: false },
        xAxis: {
          categories: ['<span style="font-size: 13px; font-weight: bold; fill: #c4b5fd;">Results</span>'],
          labels: { style: { color: '#c4b5fd', fontSize: '13px' } }
        },
        yAxis: {
          gridLineColor: 'rgba(139, 92, 246, 0.1)',
          plotBands: [
            { from: 0, to: 33.33, color: 'rgba(236, 72, 153, 0.15)', label: { text: 'Low', style: { color: '#c4b5fd', fontSize: '10px' } } },
            { from: 33.33, to: 66.66, color: 'rgba(139, 92, 246, 0.15)', label: { text: 'Medium', style: { color: '#c4b5fd', fontSize: '10px' } } },
            { from: 66.66, to: 100, color: 'rgba(16, 185, 129, 0.15)', label: { text: 'High', style: { color: '#c4b5fd', fontSize: '10px' } } }
          ],
          labels: { format: '{value}%', style: { color: '#c4b5fd' } },
          title: null,
          max: 100
        },
        plotOptions: {
          series: {
            pointPadding: 0.25,
            borderWidth: 0,
            targetOptions: { width: '200%' },
            animation: { duration: 800 }
          }
        },
        series: [
          { name: 'Up', data: [{ y: totals.totalUpPercentage, target: 50, color: '#10b981', targetColor: '#064e3b' }] },
          { name: 'Down', data: [{ y: totals.totalDownPercentage, target: 50, color: '#ec4899', targetColor: '#831843' }] },
          { name: 'Draw', data: [{ y: totals.totalDrawPercentage, target: 0, color: '#f59e0b', targetColor: '#78350f' }] }
        ],
        tooltip: {
          backgroundColor: '#1a1535',
          borderColor: '#8b5cf6',
          borderRadius: 8,
          style: { color: '#f3f0ff', fontFamily: 'Inter, sans-serif' },
          pointFormat: '<b>{series.name}</b><br/>Actual: <b>{point.y}%</b><br/>Target: <b>{point.target}%</b>'
        },
        credits: { enabled: false },
        exporting: { enabled: false }
      });

      const categories = hourlyData.map(h => h.hour);
      const upData = hourlyData.map(h => h.up);
      const downData = hourlyData.map(h => h.down);
      const drawData = hourlyData.map(h => h.draw);

      // ---------- COLUMN CHART OPTIONS (Purple Theme) ----------
      setColumnChartOptions({
        chart: {
          type: 'column',
          backgroundColor: 'transparent',
          height: 400,
          spacingTop: 20,
          spacingBottom: 40,
          zoomType: 'x',
          panning: true,
          panKey: 'shift',
          style: { fontFamily: 'Inter, sans-serif' }
        },
        title: {
          text: 'Hourly Insights',
          align: 'center',
          style: { color: '#c4b5fd', fontSize: '16px', fontWeight: '600', letterSpacing: '0.05em' }
        },
        subtitle: {
          text: '← Drag scrollbar to explore more hours →',
          style: { color: '#6b6b8d', fontSize: '11px', fontStyle: 'italic' }
        },
        xAxis: {
          categories: categories,
          labels: { style: { color: '#c4b5fd', fontSize: '11px' }, step: 1, rotation: 0 },
          lineColor: 'rgba(139, 92, 246, 0.3)',
          tickColor: 'rgba(139, 92, 246, 0.3)',
          min: 0,
          max: Math.min(4, categories.length - 1)
        },
        yAxis: {
          title: { text: null },
          gridLineColor: 'rgba(139, 92, 246, 0.1)',
          labels: { style: { color: '#c4b5fd' } },
          min: 0,
          allowDecimals: false
        },
        legend: {
          enabled: true,
          align: 'center',
          verticalAlign: 'top',
          y: 0,
          itemStyle: { color: '#c4b5fd', fontSize: '12px', fontFamily: 'Inter, sans-serif' },
          symbolRadius: 6,
          symbolHeight: 10,
          symbolWidth: 10
        },
        plotOptions: {
          column: {
            borderWidth: 0,
            borderRadius: 6,
            dataLabels: {
              enabled: true,
              color: '#f3f0ff',
              style: { textOutline: 'none', fontSize: '10px', fontWeight: '600' }
            },
            pointPadding: 0.1,
            groupPadding: 0.1,
            animation: { duration: 800 }
          }
        },
        series: [
          { name: 'Up', data: upData, color: '#10b981' },
          { name: 'Down', data: downData, color: '#ec4899' },
          { name: 'Draw', data: drawData, color: '#f59e0b' }
        ],
        scrollbar: {
          enabled: true,
          barBackgroundColor: '#1a1535',
          barBorderRadius: 8,
          barBorderWidth: 0,
          buttonBackgroundColor: '#0f0f1f',
          buttonBorderWidth: 0,
          buttonBorderRadius: 6,
          trackBackgroundColor: '#0a0a1a',
          trackBorderWidth: 0,
          rifleColor: '#8b5cf6',
          height: 16,
          liveRedraw: true,
          minWidth: 20,
          showFull: true,
          margin: 10
        },
        credits: { enabled: false },
        tooltip: {
          backgroundColor: '#1a1535',
          borderColor: '#8b5cf6',
          borderRadius: 8,
          style: { color: '#f3f0ff', fontFamily: 'Inter, sans-serif' },
          shared: true,
          formatter: function() {
            const hourIndex = this.points[0].point.index;
            const hourData = hourlyData[hourIndex];
            return `
              <b style="color: #c4b5fd;">${this.x}</b><br/>
              <span style="color:#10b981">●</span> Up: ${hourData.up} (Streak: ${hourData.up_streak})<br/>
              <span style="color:#ec4899">●</span> Down: ${hourData.down} (Streak: ${hourData.down_streak})<br/>
              <span style="color:#f59e0b">●</span> Draw: ${hourData.draw} (Streak: ${hourData.draw_streak})
            `;
          }
        },
        exporting: { enabled: false },
        navigation: { buttonOptions: { enabled: false } }
      });

      // ---------- HEATMAP CHART OPTIONS (Purple Theme) ----------
      const heatmapData = [];
      const resultTypes = ['Up', 'Down', 'Draw'];
      
      hourlyData.forEach((hourData, hourIndex) => {
        heatmapData.push([hourIndex, 0, hourData.up_streak]);
        heatmapData.push([hourIndex, 1, hourData.down_streak]);
        heatmapData.push([hourIndex, 2, hourData.draw_streak]);
      });

      const maxStreak = Math.max(...heatmapData.map(d => d[2]));

      setHeatmapChartOptions({
        chart: {
          type: 'heatmap',
          backgroundColor: 'transparent',
          height: 300,
          spacingTop: 20,
          marginTop: 60,
          marginBottom: 80,
          zoomType: 'x',
          panning: true,
          panKey: 'shift',
          style: { fontFamily: 'Inter, sans-serif' }
        },
        title: {
          text: 'Streak Intensity Map',
          align: 'center',
          style: { color: '#c4b5fd', fontSize: '16px', fontWeight: '600', letterSpacing: '0.05em' }
        },
        subtitle: {
          text: 'Darker colors indicate longer streaks',
          style: { color: '#6b6b8d', fontSize: '11px', fontStyle: 'italic' }
        },
        xAxis: {
          categories: categories,
          labels: { style: { color: '#c4b5fd', fontSize: '11px' }, step: 1, rotation: 0 },
          lineColor: 'rgba(139, 92, 246, 0.3)',
          tickLength: 5,
          min: 0,
          max: Math.min(4, categories.length - 1)
        },
        yAxis: {
          categories: resultTypes,
          title: null,
          labels: { style: { color: '#c4b5fd', fontSize: '12px' } },
          reversed: true,
          gridLineColor: 'rgba(139, 92, 246, 0.1)'
        },
        colorAxis: {
          min: 0,
          max: maxStreak,
          stops: [
            [0, '#1a1535'],
            [0.3, '#8b5cf6'],
            [0.6, '#a78bfa'],
            [1, '#c4b5fd']
          ],
          labels: { style: { color: '#c4b5fd' } }
        },
        legend: {
          align: 'right',
          layout: 'vertical',
          verticalAlign: 'middle',
          symbolHeight: 150,
          itemStyle: { color: '#c4b5fd' }
        },
        tooltip: {
          backgroundColor: '#1a1535',
          borderColor: '#8b5cf6',
          borderRadius: 8,
          style: { color: '#f3f0ff', fontFamily: 'Inter, sans-serif' },
          formatter: function() {
            return `<b>${this.series.xAxis.categories[this.point.x]}</b><br/>
                    <b style="color: #c4b5fd;">${this.series.yAxis.categories[this.point.y]}</b> streak: <b>${this.point.value}</b>`;
          }
        },
        series: [{
          name: 'Streak',
          borderWidth: 2,
          borderColor: '#0f0f1f',
          data: heatmapData,
          dataLabels: {
            enabled: true,
            color: '#f3f0ff',
            style: { textOutline: 'none', fontSize: '11px', fontWeight: 'bold' },
            formatter: function() { return this.point.value > 0 ? this.point.value : ''; }
          },
          animation: { duration: 800 }
        }],
        scrollbar: {
          enabled: true,
          barBackgroundColor: '#1a1535',
          barBorderRadius: 8,
          barBorderWidth: 0,
          buttonBackgroundColor: '#0f0f1f',
          buttonBorderWidth: 0,
          buttonBorderRadius: 6,
          trackBackgroundColor: '#0a0a1a',
          trackBorderWidth: 0,
          rifleColor: '#8b5cf6',
          height: 16,
          liveRedraw: true,
          minWidth: 20,
          showFull: true,
          margin: 10
        },
        credits: { enabled: false },
        exporting: { enabled: false },
        navigation: { buttonOptions: { enabled: false } }
      });

    } catch (error) {
      console.error("Chart Error:", error);
    }
  };

  useEffect(() => {
    if (isDrawHistoryOpen) getDailyDrawHistory();
  }, [isDrawHistoryOpen]);

  return (
    <Modal open={isDrawHistoryOpen} onClose={closeDrawHistory} closeAfterTransition>
      <Fade in={isDrawHistoryOpen}>
        <Box sx={style}>
          {/* Close Button */}
          <motion.button
            onClick={closeDrawHistory}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            style={{
              position: 'absolute',
              top: '20px',
              right: '24px',
              background: 'rgba(139, 92, 246, 0.1)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              color: '#c4b5fd',
              fontSize: '24px',
              cursor: 'pointer',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease'
            }}
          >
            &times;
          </motion.button>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 3, 
            mt: 4, 
            p: { xs: 1, md: 2 }
          }}>
            {/* Crypto Chart Widget */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box sx={{ 
                width: '100%', 
                background: 'rgba(25, 20, 45, 0.4)',
                borderRadius: '16px',
                overflow: 'hidden',
                mb: 2
              }}>
                <gecko-coin-price-chart-widget 
                  locale="en" 
                  dark-mode="true" 
                  transparent-background="true" 
                  initial-currency="krw"
                  outlined="true"
                />
              </Box>
            </motion.div>

            <Divider sx={{ bgcolor: 'rgba(139, 92, 246, 0.2)' }} />

            {/* Pie Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Box sx={{ 
                background: 'rgba(25, 20, 45, 0.3)',
                borderRadius: '16px',
                padding: '8px'
              }}>
                <HighchartsReact highcharts={Highcharts} options={pieChartOptions} />
              </Box>
            </motion.div>
            
            <Divider sx={{ bgcolor: 'rgba(139, 92, 246, 0.2)' }} />
            
            {/* Bullet Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Box sx={{ 
                background: 'rgba(25, 20, 45, 0.3)',
                borderRadius: '16px',
                padding: '8px'
              }}>
                <HighchartsReact highcharts={Highcharts} options={bulletChartOptions} />
              </Box>
            </motion.div>
            
            <Divider sx={{ bgcolor: 'rgba(139, 92, 246, 0.2)' }} />
            
            {/* Heatmap Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Box sx={{ 
                background: 'rgba(25, 20, 45, 0.3)',
                borderRadius: '16px',
                padding: '8px'
              }}>
                <HighchartsReact highcharts={Highcharts} options={heatmapChartOptions} />
              </Box>
            </motion.div>
            
            <Divider sx={{ bgcolor: 'rgba(139, 92, 246, 0.2)' }} />
            
            {/* Column Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Box sx={{ 
                background: 'rgba(25, 20, 45, 0.3)',
                borderRadius: '16px',
                padding: '8px'
              }}>
                <HighchartsReact highcharts={Highcharts} options={columnChartOptions} />
              </Box>
            </motion.div>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}